import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown, ChevronRight, Funnel, GitBranch, Minus } from 'lucide-react'

import { meetingShare, rowRateComparison } from './comparisons'
import DimensionIcon from './DimensionIcon'
import {
  availableSplits,
  type CohortPath,
  type CohortStep,
  cohortValueLabel,
  filtersForPath,
  isMeetingSplitDimension,
  MAX_SPLITS,
  type MeetingSplitDimension,
  type Metric,
  metricPhrase,
  pathKey,
  rowMeasurement,
  sourceSelectionForPath,
  type SplitDimension,
  splitLabel,
  type TreeSplits,
} from './exploration'
import { channelRules, labelSourceOrder, llmRoutes, validLabelSources } from './labelSources'
import { MeetingList } from './MetadataTables'
import { filterDimensions, formatCount, validDimensionValue } from './model'
import { ErrorState, LoadingState, StaleNotice } from './shared'
import { availableSharedSplits, MAX_SHARED_SPLITS } from './sharedSplits'
import { isTransientQueryError, useAttributionCohorts } from './useAttributionObservability'

import styles from './MetricExplorer.module.scss'

import type { AttributionFilters, AttributionSummary } from '@/api/attributionObservability'

type TreeProps = {
  filters: AttributionFilters
  folded?: ReadonlySet<string>
  onFold?: (key: string, folded: boolean) => void
  axes?: MeetingSplitDimension[]
  branchBudget?: number
  leafBudget?: number
  rowOrder?: number[]
  rootFilters?: React.ReactNode
  metric: Metric
  splits: TreeSplits
  onSplit: (path: CohortPath, dimension: SplitDimension | null) => void
  onFilter: (path: CohortPath) => void
  onViewMeetings: (path: CohortPath) => void
  onCloseMeetings: () => void
  selectedPath?: CohortPath
  meetingRequest?: number
}

type SplitCandidate = { depth: number; order: number[] }
const SharedSplitContext = createContext<{
  register: (key: string, candidate: SplitCandidate) => () => void
  owners: ReadonlySet<string>
} | null>(null)

const SplitMenu = ({
  path,
  label,
  filters,
  axes,
  splits,
  onSplit,
}: Pick<TreeProps, 'filters' | 'axes' | 'splits' | 'onSplit'> & {
  path: CohortPath
  label: string
}) => {
  const current = axes ? axes[path.length] : splits[pathKey(path)]
  const choices = axes
    ? path.length < MAX_SHARED_SPLITS
      ? availableSharedSplits(filters, axes.slice(0, path.length))
      : []
    : availableSplits(filters, path)
  if (!choices.length) return null
  const atLimit = !axes && Object.keys(splits).length >= MAX_SPLITS && !(pathKey(path) in splits)
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className={styles.splitButton}
          type="button"
          disabled={atLimit}
          data-split-level={axes ? path.length : undefined}
          aria-label={axes ? `Split level ${path.length + 1}` : `Split ${label} by`}
          title={
            atLimit ? 'Remove a split before adding another' : axes ? 'Applies to every item at this level' : undefined
          }
        >
          <GitBranch size={14} aria-hidden="true" />
          <span className={axes && path.length > 0 ? styles.splitDefaultLabel : undefined}>
            Split by{current ? ` ${splitLabel(current)}` : ''}
          </span>
          {axes && path.length > 0 && (
            <span className={styles.splitHoverLabel}>
              Split this level{current ? ` by ${splitLabel(current)}` : ''}
            </span>
          )}
          <ChevronDown size={12} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={styles.splitMenu} sideOffset={6} align="end">
          <DropdownMenu.Label className={styles.menuLabel}>
            Split {axes ? `all items at level ${path.length + 1}` : label} by
          </DropdownMenu.Label>
          {choices.map((dimension) => (
            <DropdownMenu.Item key={dimension} className={styles.menuItem} onSelect={() => onSplit(path, dimension)}>
              <span className={styles.menuChoice}>
                <DimensionIcon dimension={dimension} />
                {splitLabel(dimension)}
              </span>
              {current === dimension && <Check size={14} aria-hidden="true" />}
            </DropdownMenu.Item>
          ))}
          {current && (
            <>
              <DropdownMenu.Separator className={styles.menuSeparator} />
              <DropdownMenu.Item className={styles.menuItem} onSelect={() => onSplit(path, null)}>
                Remove split <Minus size={14} aria-hidden="true" />
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

const CohortNode = ({
  path,
  summary,
  parent,
  ...props
}: TreeProps & {
  path: CohortPath
  summary: AttributionSummary
  parent?: AttributionSummary
}) => {
  const meetingButton = useRef<HTMLButtonElement>(null)
  const coverageHelpId = useId()
  const isRoot = path.length === 0
  const last = path[path.length - 1]
  const rootLabel =
    props.axes && filterDimensions.some((dimension) => props.filters[dimension])
      ? 'Filtered meetings'
      : 'All meetings'
  const label = isRoot ? rootLabel : cohortValueLabel(last[0], last[1])
  const context = isRoot ? rootLabel : path.map(([dimension, value]) => cohortValueLabel(dimension, value)).join(' / ')
  const supportedPath = path.every(
    ([dimension, value]) => !isMeetingSplitDimension(dimension) || validDimensionValue(dimension, value)
  )
  const sharedSplit = useContext(SharedSplitContext)
  const registerSplit = sharedSplit?.register
  const candidateKey = pathKey(path)
  const orderKey = JSON.stringify(props.rowOrder ?? [])
  const eligible = Boolean(props.axes && supportedPath && path.length < MAX_SHARED_SPLITS)
  useEffect(() => {
    if (eligible)
      return registerSplit?.(candidateKey, {
        depth: path.length,
        order: JSON.parse(orderKey),
      })
  }, [registerSplit, candidateKey, path.length, orderKey, eligible])
  const split = props.axes ? props.axes[path.length] : props.splits[pathKey(path)]
  const foldKey = JSON.stringify([path, split])
  const collapsed = props.folded?.has(foldKey) ?? false
  const setCollapsed = (value: boolean) => props.onFold?.(foldKey, value)
  const inspecting = props.selectedPath !== undefined && pathKey(props.selectedPath) === pathKey(path)
  const scopedFilters = filtersForPath(props.filters, path)
  const measurement = rowMeasurement(summary, props.metric, scopedFilters, path)
  const sourceSelection = sourceSelectionForPath(path)
  const meetingMetric = props.metric === 'meetings' && !sourceSelection
  const parentPath = path.slice(0, -1)
  const parentLabel =
    parentPath.map(([dimension, value]) => cohortValueLabel(dimension, value)).join(' / ') || rootLabel
  const comparison = rowRateComparison(
    summary,
    parent,
    props.metric,
    scopedFilters,
    filtersForPath(props.filters, parentPath),
    path,
    parentLabel
  )
  const share = meetingShare(summary, parent)
  const bar = meetingMetric ? (isRoot ? 100 : share) : measurement.percent
  const baseline = comparison ? `Parent average: ${comparison.parentRate.toFixed(1)}% (${parentLabel})` : undefined
  const toggleSplit = (nodePath: CohortPath, dimension: SplitDimension | null) => {
    setCollapsed(false)
    props.onSplit(nodePath, dimension)
  }
  const closeMeetings = () => {
    props.onCloseMeetings()
    meetingButton.current?.focus()
  }
  return (
    <li className={styles.treeNode}>
      <div
        data-row-level={props.axes ? path.length : undefined}
        className={`${styles.treeRow} ${isRoot ? styles.rootRow : ''}`}
      >
        <div className={styles.nodeIdentity}>
          {split ? (
            <button
              type="button"
              className={styles.disclosure}
              aria-expanded={!collapsed}
              aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${context}`}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </button>
          ) : null}
          <div>
            <span className={styles.nodeName}>
              <DimensionIcon dimension={last?.[0] ?? 'meetings'} value={last?.[1]} />
              <span>{label}</span>
              {!isRoot && supportedPath && isMeetingSplitDimension(last[0]) && (
                <button
                  type="button"
                  className={styles.focusButton}
                  aria-label={`Filter to ${context}`}
                  title="Filter to this branch"
                  onClick={() => props.onFilter(path)}
                >
                  <Funnel size={14} />
                </button>
              )}
            </span>
            {isRoot && props.rootFilters}
            <span className={styles.nodeMeta}>
              {measurement.meetings !== null ? (
                <button
                  ref={meetingButton}
                  type="button"
                  className={styles.meetingCount}
                  disabled={!supportedPath}
                  title={!supportedPath ? 'Update the dashboard to inspect this category.' : undefined}
                  aria-label={`View meetings in ${context}`}
                  aria-expanded={inspecting}
                  aria-controls={inspecting ? 'cohort-meetings' : undefined}
                  onClick={() => (inspecting ? closeMeetings() : props.onViewMeetings(path))}
                >
                  <span>{formatCount(measurement.meetings)} meetings</span>
                  {inspecting ? (
                    <ChevronDown size={13} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={13} aria-hidden="true" />
                  )}
                </button>
              ) : (
                <span>Meeting count unavailable</span>
              )}
              {measurement.sourceMeasured !== null && (
                <span
                  className={styles.sourceCoverage}
                  title={measurement.coverageHelp ?? undefined}
                  aria-describedby={coverageHelpId}
                  tabIndex={0}
                >
                  {' '}
                  · {formatCount(measurement.sourceMeasured)} / {formatCount(summary.meeting_count)} meetings with data
                  <span id={coverageHelpId} className={styles.srOnly}>
                    {measurement.coverageHelp}
                  </span>
                </span>
              )}
            </span>
            {measurement.claim && (
              <span className={styles.nodeMeta} title={measurement.claim.coverageHelp}>
                LLM claim rate: {measurement.claim.value} · {measurement.claim.detail}
              </span>
            )}
          </div>
        </div>
        <div className={styles.nodeMetric}>
          {measurement.cue && <span className={styles.measureCue}>{measurement.cue}</span>}
          <div className={styles.metricLine}>
            <strong>
              {measurement.value}
              {meetingMetric && parent && (
                <span className={styles.parentCount}> / {formatCount(parent.meeting_count)}</span>
              )}
            </strong>
          </div>
          {meetingMetric && share !== null && (
            <small title={`${share.toFixed(1)}% of ${parentLabel}`}>{share.toFixed(1)}% of parent</small>
          )}
          {!meetingMetric && (
            <small>
              {measurement.percent === null && !sourceSelection
                ? `${formatCount(measurement.measured)} meetings measured`
                : measurement.detail}
            </small>
          )}
        </div>
        <div className={styles.barMetric}>
          <div
            className={styles.barTrack}
            role={bar !== null ? 'img' : undefined}
            aria-label={bar !== null ? `${bar.toFixed(1)}%${baseline ? `. ${baseline}` : ''}` : undefined}
            title={baseline}
          >
            {bar !== null && (
              <span className={styles.barFill} style={{ width: `${Math.max(0, Math.min(100, bar))}%` }} />
            )}
            {comparison && (
              <span
                className={styles.parentMarker}
                style={{
                  left: `${Math.max(0, Math.min(100, comparison.parentRate))}%`,
                }}
                aria-hidden="true"
              />
            )}
          </div>
          {comparison && (
            <span className={styles.comparison} title={comparison.description} aria-label={comparison.description}>
              {comparison.label}
            </span>
          )}
        </div>
        <div className={styles.nodeActions}>
          {supportedPath && (!props.axes || sharedSplit?.owners.has(candidateKey)) && (
            <SplitMenu
              path={path}
              label={context}
              filters={props.filters}
              axes={props.axes}
              splits={props.splits}
              onSplit={toggleSplit}
            />
          )}
        </div>
      </div>
      {inspecting && measurement.meetings !== null && (
        <div className={styles.inlineMeetings}>
          <MeetingList
            key={props.meetingRequest}
            filters={filtersForPath(props.filters, path)}
            sourceSelection={sourceSelection}
            variant="inline"
            captureCoverage={props.metric === 'ax_capture'}
            inspection={{ label: context, onClose: closeMeetings }}
          />
        </div>
      )}
      {split && !collapsed && <CohortLevel key={split} {...props} path={path} dimension={split} parent={summary} />}
    </li>
  )
}

type LevelProps = TreeProps & {
  path: CohortPath
  dimension: SplitDimension
  parent: AttributionSummary
}

const CohortLevel = ({ dimension, ...props }: LevelProps) => {
  if (isMeetingSplitDimension(dimension)) return <PhysicalCohortLevel {...props} dimension={dimension} />
  if (!props.parent.label_sources || (dimension !== 'label_source' && !validLabelSources(props.parent)))
    return <p className={styles.treeMessage}>Source breakdown unavailable for this selection.</p>
  const values =
    dimension === 'label_source' ? labelSourceOrder : dimension === 'channel_rule' ? channelRules : llmRoutes
  return (
    <ul
      data-cohort-level={props.axes ? props.path.length + 1 : undefined}
      className={styles.tree}
      aria-label={`Breakdown by ${splitLabel(dimension)}`}
    >
      {values.map((value) => (
        <CohortNode
          key={value}
          {...props}
          summary={props.parent}
          path={[...props.path, [dimension, value] as CohortStep]}
        />
      ))}
    </ul>
  )
}

const PhysicalCohortLevel = ({
  path,
  dimension,
  ...props
}: Omit<LevelProps, 'dimension'> & { dimension: MeetingSplitDimension }) => {
  const query = useAttributionCohorts(filtersForPath(props.filters, path), dimension)
  const displayLimit = props.axes && path.length === props.axes.length - 1 ? props.leafBudget : props.branchBudget
  if (query.isPending)
    return (
      <div className={styles.treeMessage}>
        <LoadingState label="Loading breakdown…" />
      </div>
    )
  if (query.isError && (!query.data || !isTransientQueryError(query.error)))
    return (
      <div className={styles.treeMessage}>
        <ErrorState onRetry={() => void query.refetch()} />
      </div>
    )
  return (
    <ul
      data-cohort-level={props.axes ? path.length + 1 : undefined}
      className={styles.tree}
      aria-label={`Breakdown by ${splitLabel(dimension)}`}
    >
      {query.isError && (
        <li className={styles.treeMessage}>
          <StaleNotice onRetry={() => void query.refetch()} />
        </li>
      )}
      {query.data?.groups.slice(0, displayLimit).map((group, index, displayed) => (
        <CohortNode
          key={group.value}
          {...props}
          rowOrder={[...(props.rowOrder ?? []), index]}
          leafBudget={
            props.leafBudget === undefined
              ? undefined
              : Math.floor(props.leafBudget / displayed.length) + (index < props.leafBudget % displayed.length ? 1 : 0)
          }
          branchBudget={
            props.branchBudget === undefined
              ? undefined
              : Math.floor(props.branchBudget / displayed.length) +
                (index < props.branchBudget % displayed.length ? 1 : 0)
          }
          summary={group.summary}
          path={[...path, [dimension, group.value]]}
        />
      ))}
      {(query.data?.truncated || (displayLimit !== undefined && (query.data?.groups.length ?? 0) > displayLimit)) && (
        <li className={styles.treeMessage}>
          Showing the largest groups within this branch’s display limit. Add a filter to see smaller groups.
        </li>
      )}
      {!query.data?.groups.length && <li className={styles.treeMessage}>No meetings in this branch.</li>}
    </ul>
  )
}

const Breakdown = ({
  summary,
  onReset,
  ...props
}: TreeProps & { summary: AttributionSummary; onReset: () => void }) => {
  const [candidates, setCandidates] = useState<ReadonlyMap<string, SplitCandidate>>(() => new Map())
  const register = useCallback((key: string, candidate: SplitCandidate) => {
    setCandidates((previous) => new Map(previous).set(key, candidate))
    return () => {
      setCandidates((previous) => {
        const next = new Map(previous)
        next.delete(key)
        return next
      })
    }
  }, [])
  const splitContext = useMemo(() => {
    const firstByDepth = new Map<number, string>()
    for (const [key, candidate] of Array.from(candidates).sort(([, left], [, right]) => {
      for (let index = 0; index < Math.min(left.order.length, right.order.length); index++) {
        const difference = left.order[index] - right.order[index]
        if (difference) return difference
      }
      return left.order.length - right.order.length
    })) {
      if (!firstByDepth.has(candidate.depth)) firstByDepth.set(candidate.depth, key)
    }
    return { register, owners: new Set(firstByDepth.values()) }
  }, [candidates, register])
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set())
  const onFold = (key: string, value: boolean) =>
    setFolded((previous) => {
      const next = new Set(previous)
      if (value) next.add(key)
      else next.delete(key)
      return next
    })
  return (
    <section className={styles.explorer} aria-label="Metric explorer">
      <div className={styles.explorerHeader}>
        <div>
          <h2>{props.axes ? `Explore ${metricPhrase(props.metric)}` : 'Speaker label sources'}</h2>
          <p>
            {props.axes
              ? 'Each level splits every branch by the same dimension. Click a meeting count to inspect meetings.'
              : 'Explore speaker sources and rules. These overlapping shares are separate from the daily chart.'}
          </p>
        </div>
        <button
          type="button"
          className={styles.textButton}
          onClick={() => {
            setFolded(new Set())
            onReset()
          }}
        >
          Reset tree
        </button>
      </div>
      <ul className={`${styles.tree} ${styles.rootTree}`} aria-label="Meeting cohorts">
        <SharedSplitContext.Provider value={splitContext}>
          <CohortNode {...props} folded={folded} onFold={onFold} path={[]} summary={summary} />
        </SharedSplitContext.Provider>
      </ul>
      <p className={styles.explorerHint}>
        {props.axes ? (
          <>
            The chart shows the largest combined groups across these split levels.{' '}
            <Funnel size={12} aria-hidden="true" /> filters the whole page to a branch.
          </>
        ) : (
          <>
            AX, Channel, LLM, and Unnamed show final shares of active speakers. Rules show shares within their source.
            LLM claim rate uses evaluated speakers from the latest attempt. Meeting counts can overlap.
          </>
        )}
      </p>
    </section>
  )
}

export default Breakdown
